import { observer } from "mobx-react-lite";
import { ChartStore, ChartType } from "@/stores/ui/Chart";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Eye, EyeOff, Palette, RotateCcw, ZoomIn, ZoomOut, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChartControlsProps {
	store: ChartStore;
	className?: string;
	/** "refresh" shows only refresh button, "zoom" shows only zoom, "tools" shows all tools, undefined shows all */
	variant?: "refresh" | "zoom" | "tools";
}

const CHART_TYPES: { value: ChartType; label: string }[] = [
	{ value: "line", label: "Line" },
	{ value: "spline", label: "Spline" },
	{ value: "area", label: "Area" },
	{ value: "areaspline", label: "Area Spline" },
	{ value: "column", label: "Column" },
];

const COLOR_PRESETS = [
	"#abc7ff",
	"#ff6b6b",
	"#4ecdc4",
	"#45b7d1",
	"#96ceb4",
	"#ffeaa7",
	"#a29bfe",
	"#fd79a8",
	"#00b894",
	"#e17055",
	"#0984e3",
	"#6c5ce7",
];

export const ChartControls: React.FC<ChartControlsProps> = observer(({ store, className, variant }) => {
	const showRefresh = !variant || variant === "refresh";
	const showZoom = !variant || variant === "zoom" || variant === "tools";
	const showFullTools = !variant || variant === "tools";

	return (
		<TooltipProvider>
			<div className={cn("flex flex-wrap items-center gap-2", className)}>
				{/* Refetch */}
				{showRefresh && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className="h-7 w-7 bg-transparent"
								onClick={() => store.refetch()}
								disabled={store.loading}
							>
								<RefreshCw className={cn("h-3.5 w-3.5", store.loading && "animate-spin")} />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<p>Refresh data</p>
						</TooltipContent>
					</Tooltip>
				)}

				{/* Zoom Controls */}
				{showZoom && (
					<div className="flex items-center gap-1 border rounded-md p-0.5">
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6"
									onClick={() => store.zoom.zoomOut()}
								>
									<ZoomOut className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>Zoom out to see more data</p>
							</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6"
									onClick={() => store.zoom.zoomIn()}
								>
									<ZoomIn className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>Zoom in for more detail</p>
							</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6"
									onClick={() => store.zoom.reset()}
									disabled={!store.zoom.isZoomed}
								>
									<RotateCcw className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>Reset to original view</p>
							</TooltipContent>
						</Tooltip>
					</div>
				)}

				{showFullTools && (
					<>
						{/* Chart Type Selector */}
						<Select value={store.chartType} onValueChange={value => store.setChartType(value as ChartType)}>
							<SelectTrigger className="w-[110px] h-7 text-xs bg-transparent">
								<SelectValue placeholder="Chart type" />
							</SelectTrigger>
							<SelectContent>
								{CHART_TYPES.map(type => (
									<SelectItem key={type.value} value={type.value}>
										{type.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						{/* Graph & Range Controls */}
						{(store.graphs.length > 0 || store.ranges.length > 0) && (
							<Popover>
								<PopoverTrigger asChild>
									<Button variant="outline" size="sm" className="h-7 text-xs bg-transparent">
										<Palette className="h-3.5 w-3.5 mr-1" />
										Style
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-72" align="end">
									<div className="space-y-3">
										{store.graphs.length > 0 && (
											<>
												<h4 className="font-medium text-sm">Graph Settings</h4>
												{store.graphs.map(graph => (
													<div
														key={graph.id}
														className="flex items-center gap-2 py-1 border-b last:border-b-0"
													>
														<Button
															variant="ghost"
															size="icon"
															className="h-6 w-6"
															onClick={() => graph.toggleVisible()}
														>
															{graph.visible ? (
																<Eye className="h-3 w-3" />
															) : (
																<EyeOff className="h-3 w-3 text-muted-foreground" />
															)}
														</Button>
														<Popover>
															<PopoverTrigger asChild>
																<button
																	className="h-5 w-5 rounded border"
																	style={{ backgroundColor: graph.color }}
																/>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-2" align="start">
																<div className="grid grid-cols-6 gap-1">
																	{COLOR_PRESETS.map(presetColor => (
																		<button
																			key={presetColor}
																			className={cn(
																				"h-6 w-6 rounded border-2",
																				graph.color === presetColor
																					? "border-primary"
																					: "border-transparent",
																			)}
																			style={{ backgroundColor: presetColor }}
																			onClick={() => graph.setColor(presetColor)}
																		/>
																	))}
																</div>
															</PopoverContent>
														</Popover>
														<span className="flex-1 text-sm truncate">{graph.name}</span>
													</div>
												))}
											</>
										)}
										{store.ranges.length > 0 && (
											<>
												<h4 className="font-medium text-sm">Range Settings</h4>
												{store.ranges.map(range => (
													<div
														key={range.name}
														className="flex items-center gap-2 py-1 border-b last:border-b-0"
													>
														<Button
															variant="ghost"
															size="icon"
															className="h-6 w-6"
															onClick={() => range.toggleVisible()}
														>
															{range.visible ? (
																<Eye className="h-3 w-3" />
															) : (
																<EyeOff className="h-3 w-3 text-muted-foreground" />
															)}
														</Button>
														<Popover>
															<PopoverTrigger asChild>
																<button
																	className="h-5 w-5 rounded border"
																	style={{ backgroundColor: range.color }}
																/>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-2" align="start">
																<div className="grid grid-cols-6 gap-1">
																	{COLOR_PRESETS.map(presetColor => (
																		<button
																			key={presetColor}
																			className={cn(
																				"h-6 w-6 rounded border-2",
																				range.color === presetColor
																					? "border-primary"
																					: "border-transparent",
																			)}
																			style={{ backgroundColor: presetColor }}
																			onClick={() => range.setColor(presetColor)}
																		/>
																	))}
																</div>
															</PopoverContent>
														</Popover>
														<span className="flex-1 text-sm truncate">{range.name}</span>
													</div>
												))}
											</>
										)}
									</div>
								</PopoverContent>
							</Popover>
						)}
					</>
				)}
			</div>
		</TooltipProvider>
	);
});
